/**
 * WAF Pulse analytics (Owl) — geolocation performance diagram: latency /
 * availability over time, sourced from RUM or synthetic edge probing.
 * Service-scoped (path `/services/:service_id/waf/…`), `GET /services/:service_id/waf/pulse/geolocation`.
 *
 * One endpoint serves both the Static and Dynamic views via `data_type`
 * (`static` | `dynamic`); the vendor path is `pulseReport/geolocation`.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/** One series — `series` maps each requested metric (latency/availability) to per-bucket values. */
export interface WafPulseDataset {
  label: string;
  type?: string;
  series: Record<string, (number | null)[]>;
}

/** Pulse diagram: shared x-axis (`labels`, unix ms) + per-platform/geo datasets. */
export interface WafPulseDiagram {
  labels: number[];
  datasets: WafPulseDataset[];
}

/** Query filter for the Pulse geolocation diagram. CSV fields are pre-joined strings. */
export interface WafPulseFilter {
  /** Start unix seconds. */
  start_time: number;
  /** End unix seconds. */
  end_time: number;
  /** Bucket seconds (default 86400; auto-bucketed when omitted). */
  interval?: number;
  /** Metrics CSV — "latency" | "availability" (default latency). */
  types?: string;
  /** Sources CSV — "waver" | "rum" | "embedded" (default all). */
  sources?: string;
  /** "static" | "dynamic" (default static). */
  data_type?: string;
  /** Domain names CSV (subdomain FQDNs); omitted = all. */
  domains?: string;
  /** CDN platform IDs CSV; omitted = all. */
  platforms?: string;
  /** Geo drill-down (optional). */
  country?: string;
  region?: string;
  city?: string;
}

/** Pulse geolocation diagram. */
export function useWafPulseGeoDiagram(
  serviceId: string | undefined,
  filter: WafPulseFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafPulseDiagram>({
    queryKey: wafKeys.pulseDiagram(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.start_time && !!filter.end_time && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafPulseDiagram>(
        await serviceApiV3.get(`/services/${serviceId}/waf/pulse/geolocation`, { params }),
      ),
  });
}
